import { LoadingButton } from "@mui/lab";
import { Alert, Box } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import ConfirmationModal from "components/modals/confirmation-modal";
import { BackgroundLetterAvatarsSquare } from "components/shared/shared";
import {
  GetUserIfLoggedIn,
  getActiveOrganisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { UserMembership } from "data/models";
import { userAcceptInvitation, userRejectInvitation } from "data/mutations-common";
import { fetchUserInvitations } from "data/queries-common";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";

export const UserInvitations = () => {
  const [identity, setIdentity] = useIdentity();
  const identityId = resolveIdentityId(identity);

  const queryClient = useQueryClient();

  const activeOrg = getActiveOrganisation(identity);

  // Use React Query to fetch user invitations
  const userInvitationsQuery = useQuery({
      queryKey: [QueryKeys.userInvitations, identityId],
      queryFn: fetchUserInvitations,
    });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] =
    useState<UserMembership | null>(null);

  // Modal confirmation rejected
  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedInvitation(null);
  };

  const handleDelete = (invitation: UserMembership) => {
    setSelectedInvitation(invitation);
    setDeleteOpen(true);
  };

  // Accept invitation mutation
  const acceptInvitationMutation = useMutation({
      mutationFn: (invitation: UserMembership) =>
        userAcceptInvitation(invitation.organisationId),
      onSuccess: (_, invitation) => {
          // Update React Query cache - remove the accepted invitation from the list
          queryClient.setQueryData<UserMembership[]>(
            [QueryKeys.userInvitations, identityId],
            (oldData) => {
              if (!oldData) return [];
              return oldData.filter(
                (inv) => inv.organisationId !== invitation.organisationId
              );
            }
          );

          // Update identity state to include the new organization
          setIdentity((prevIdentity) => {
            const user = GetUserIfLoggedIn(prevIdentity);
            if (user) {
              return {
                ...user,
                organisations: [
                  ...user.organisations,
                  {
                    name: invitation.organisationName,
                    organisationId: invitation.organisationId,
                    active: false,
                  },
                ],
              };
            }
            return prevIdentity;
          });
        },
    });

  // Reject invitation mutation
  const rejectInvitationMutation = useMutation({
      mutationFn: (invitation: UserMembership) =>
        userRejectInvitation(invitation.organisationId),
      onSuccess: (_, invitation) => {
          // Update React Query cache - remove the rejected invitation from the list
          queryClient.setQueryData<UserMembership[]>(
            [QueryKeys.userInvitations, identityId],
            (oldData) => {
              if (!oldData) return [];
              return oldData.filter(
                (inv) => inv.organisationId !== invitation.organisationId
              );
            }
          );
          handleDeleteClose();
        },
    });

  const rows = (userInvitationsQuery.data || [])
    .filter((invitation) => invitation.status === "Pending")
    .map((invitation): any => {
      return {
        id: invitation.organisationName,
        invitation: invitation,
        organisationName: invitation.organisationName,
        actions: invitation.status,
      };
    })
    .sort((a, b) => {
      const nameA = a.organisationName.toUpperCase();
      const nameB = b.organisationName.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });

  const columns = [
    {
      flex: 3,
      field: "organisationName",
      headerName: "Organisation Name",
      disableColumnSelector: true,
      disableSelectionOnClick: true,
      hideable: false,
      renderCell: (params: any) => (
        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
          }}
        >
          <BackgroundLetterAvatarsSquare
            name={params.row.organisationName}
            activeOrg={activeOrg}
            isLoading={userInvitationsQuery.isPending}
          />
          {params.row.organisationName}
        </div>
      ),
    },
    {
      flex: 1,
      field: "actions",
      headerName: "Actions",

      renderCell: (params: any) => (
        <Box style={{ display: "flex", gap: "50px", margin: "5px" }}>
          <LoadingButton
            size="medium"
            disabled={
              params.row.invitation.status === "Accepted" ||
              acceptInvitationMutation.isPending
            }
            color={
              params.row.invitation.status === "Accepted"
                ? "secondary"
                : "success"
            }
            variant="outlined"
            onClick={() =>
              acceptInvitationMutation.mutate(params.row.invitation)
            }
          >
            {params.row.invitation.status === "Accepted"
              ? "Accepted"
              : "Accept"}
          </LoadingButton>

          <LoadingButton
            size="medium"
            disabled={
              params.row.invitation.status === "Accepted" ||
              rejectInvitationMutation.isPending
            }
            color={
              params.row.invitation.status === "Accepted" ? "error" : "error"
            }
            variant="outlined"
            onClick={() => {
              handleDelete(params.row.invitation);
            }}
          >
            {params.row.invitation.status === "Accepted"
              ? "Accepted"
              : "Reject"}
          </LoadingButton>
        </Box>
      ),
    },
  ];

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  return (
    <>
      <Box sx={{ m: "20px" }} />
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          height: "100%",
          position: "relative",
        }}
      >
        {acceptInvitationMutation.isSuccess && (
          <Box marginBottom={1} pt={2}>
            <Alert variant="filled" severity="success">
              Invitation from "
              {acceptInvitationMutation.variables?.organisationName}" has been
              accepted
            </Alert>
          </Box>
        )}

        {acceptInvitationMutation.isError && (
          <Box marginBottom={1} pt={2}>
            <Alert variant="filled" severity="error">
              {(acceptInvitationMutation.error as Error)?.message ||
                "An error occurred while accepting the invitation"}
            </Alert>
          </Box>
        )}

        {rejectInvitationMutation.isSuccess && (
          <Box marginBottom={1} pt={2}>
            <Alert variant="filled" severity="success">
              Invitation has been rejected
            </Alert>
          </Box>
        )}

        {rejectInvitationMutation.isError && (
          <Box marginBottom={1} pt={2}>
            <Alert variant="filled" severity="error">
              {(rejectInvitationMutation.error as Error)?.message ||
                "An error occurred while rejecting the invitation"}
            </Alert>
          </Box>
        )}

        {userInvitationsQuery.isError && (
          <Box marginBottom={1} pt={2}>
            <Alert variant="filled" severity="error">
              An error occurred while fetching the invitations
            </Alert>
          </Box>
        )}

        <ConfirmationModal
          isLoading={rejectInvitationMutation.isPending}
          isError={rejectInvitationMutation.isError}
          open={deleteOpen}
          header={`Reject this invitation?`}
          body={`You won't be able to join "${selectedInvitation?.organisationName}" again unless you receive another invitation.`}
          leftButtonText={"CANCEL"}
          leftButtonColor="primary"
          rightButtonText={"REJECT INVITATION"}
          rightButtonColor="error"
          handleClose={() => handleDeleteClose()}
          handleLeftButton={() => handleDeleteClose()}
          handleRightButton={() => {
            if (selectedInvitation) {
              rejectInvitationMutation.mutate(selectedInvitation);
            }
          }}
        ></ConfirmationModal>

        <DataGridPremium
          checkboxSelection={false}
          loading={userInvitationsQuery.isPending}
          rows={rows}
          columns={columns}
          autoHeight={true}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
        />
      </Box>
    </>
  );
};
